export type FrontendErrorsSourceStatus = "ok" | "degraded" | "unknown";

export type FrontendErrorsSourceReason = "index_missing" | "timeout" | "shards_failed" | "groups_truncated" | "transport";

export type FrontendErrorsSource = "frontend" | "backend";

export type FrontendErrorsCategory = "technical_fault" | "business_rejection" | "user_cancelled" | "warning" | "unknown";

export type FrontendErrorsSourceState = {
    sourceStatus: FrontendErrorsSourceStatus;
    reason: FrontendErrorsSourceReason | null;
    collector: { status: FrontendErrorsSourceStatus; checkedAt: number | null };
    countsAreLowerBound: boolean;
    source: FrontendErrorsSource;
};

export type FrontendErrorsDistribution = {
    buckets: { key: string; count: number }[];
    other: number;
};

export type FrontendErrorsCoverageGap = {
    from: number;
    to: number;
    reason: string;
    status: string;
    devices: number;
};

export type FrontendErrorsCompleteness =
    | { status: "gaps_reported"; gaps: FrontendErrorsCoverageGap[]; gapsTruncated: boolean }
    | { status: "no_gaps_reported"; gaps: []; gapsTruncated: false }
    | { status: null; gaps: null; gapsTruncated: null };

export type FrontendErrorsClientLoss = {
    overflow: number | null;
    expired: number | null;
    dropped: number | null;
};

export type FrontendErrorsSummary = FrontendErrorsSourceState & {
    asOf: number;
    from: number;
    to: number;
    total: number | null;
    byCategory: FrontendErrorsDistribution | null;
    affectedDevicesApprox: number | null;
    distinctFingerprints: number | null;
    freshness: { lastReceivedAt: number | null; lastIndexedAt: number | null } | null;
    clientLoss: FrontendErrorsClientLoss | null;
    consequencesSuppressed: number | null;
    clientLossIsEstimate: true;
    completeness: FrontendErrorsCompleteness;
};

export type FrontendErrorsSeriesBucket = {
    start: number;
    end: number;
    madridLabel: string;
    count: number | null;
    byCategory: { category: string; count: number | null }[] | null;
    devices: number | null;
};

export type FrontendErrorsSeries = FrontendErrorsSourceState & {
    asOf: number;
    from: number;
    to: number;
    bucketMs: number;
    buckets: FrontendErrorsSeriesBucket[] | null;
};

export type FrontendErrorGroup = {
    fingerprint: string | null;
    category: string | null;
    errorCode: string | null;
    count: number;
    affectedDevicesApprox: number | null;
    firstSeenAt: number | null;
    lastSeenAt: number | null;
    errorClass: string | null;
    route: string | null;
    httpStatus: number | null;
    releases: string[] | null;
    sampleMessage: string | null;
    messageTemplate: string | null;
};

export type FrontendErrorsGroups = FrontendErrorsSourceState & {
    asOf: number;
    total: number | null;
    groups: FrontendErrorGroup[] | null;
};

export type FrontendErrorFrame = {
    functionName: string | null;
    asset: string | null;
    line: number | null;
    column: number | null;
};

export type FrontendErrorEvent = {
    receiptId: string;
    effectiveOccurredAt: number;
    occurredAt: number | null;
    receivedAt: number | null;
    category: string | null;
    kind: string | null;
    errorCode: string | null;
    errorClass: string | null;
    fingerprint: string | null;
    route: string | null;
    release: string | null;
    platform: string | null;
    source: string | null;
    trust: string | null;
    message: string | null;
    httpStatus: number | null;
    deviceId: string | null;
    frames: FrontendErrorFrame[] | null;
};

export type FrontendErrorsEvents = FrontendErrorsSourceState & {
    asOf: number;
    events: FrontendErrorEvent[] | null;
    nextCursor: string | null;
};

export type FrontendErrorsQueryParams = {
    workspaceId: string;
    source: FrontendErrorsSource;
    from: number;
    to: number;
    category: FrontendErrorsCategory | null;
    errorCode: string | null;
    release: string | null;
};

export type FrontendErrorsPopulationParams = FrontendErrorsQueryParams & { asOf: number };

// The sample message per group is asked for only where it is shown (the command center card).
export type FrontendErrorsGroupsParams = FrontendErrorsPopulationParams & { includeSample: boolean };

export type FrontendErrorsEventsParams = FrontendErrorsPopulationParams & { limit: number | null; fingerprint: string | null };

export type FrontendErrorsEventsPageParams = FrontendErrorsEventsParams & { cursor: string | null };

type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

// Wire shapes: the fields added by vb-metrics#38 (U1) may be absent when an older reader answers.
export type FrontendErrorsSummaryWire = Optional<FrontendErrorsSummary, "distinctFingerprints">;

export type FrontendErrorsSeriesBucketWire = Optional<FrontendErrorsSeriesBucket, "byCategory" | "devices">;

export type FrontendErrorsSeriesWire = Omit<FrontendErrorsSeries, "buckets"> & { buckets: FrontendErrorsSeriesBucketWire[] | null };

export type FrontendErrorGroupWire = Optional<FrontendErrorGroup, "errorClass" | "route" | "httpStatus" | "releases" | "sampleMessage" | "messageTemplate">;

export type FrontendErrorsGroupsWire = Omit<FrontendErrorsGroups, "groups"> & { groups: FrontendErrorGroupWire[] | null };

export type FrontendErrorEventWire = Optional<FrontendErrorEvent, "message" | "httpStatus" | "deviceId" | "frames">;

export type FrontendErrorsEventsWire = Omit<FrontendErrorsEvents, "events"> & { events: FrontendErrorEventWire[] | null };

type Row = Record<string, unknown>;

const isRow = (value: unknown): value is Row =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const isNullableNumber = (value: unknown): value is number | null => value === null || isNumber(value);

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === "string";

const isAbsentOr = (value: unknown, guard: (candidate: unknown) => boolean): boolean =>
    value === undefined || guard(value);

const isArrayOf = <T,>(value: unknown, guard: (item: unknown) => item is T): value is T[] =>
    Array.isArray(value) && value.every((item) => guard(item));

const SOURCE_STATUSES: readonly string[] = ["ok", "degraded", "unknown"];
const SOURCE_REASONS: readonly string[] = ["index_missing", "timeout", "shards_failed", "groups_truncated", "transport"];
const SOURCES: readonly string[] = ["frontend", "backend"];

const isSourceStatus = (value: unknown): value is FrontendErrorsSourceStatus =>
    typeof value === "string" && SOURCE_STATUSES.includes(value);

const isSourceState = (value: Row): boolean =>
    isSourceStatus(value.sourceStatus)
    && (value.reason === null || (typeof value.reason === "string" && SOURCE_REASONS.includes(value.reason)))
    && isRow(value.collector)
    && isSourceStatus(value.collector.status)
    && isNullableNumber(value.collector.checkedAt)
    && typeof value.countsAreLowerBound === "boolean"
    && typeof value.source === "string"
    && SOURCES.includes(value.source);

const isDistributionBucket = (value: unknown): value is { key: string; count: number } =>
    isRow(value) && typeof value.key === "string" && isNumber(value.count);

const isDistribution = (value: unknown): boolean =>
    isRow(value) && isArrayOf(value.buckets, isDistributionBucket) && isNumber(value.other);

const isCoverageGap = (value: unknown): value is FrontendErrorsCoverageGap =>
    isRow(value)
    && isNumber(value.from)
    && isNumber(value.to)
    && typeof value.reason === "string"
    && typeof value.status === "string"
    && isNumber(value.devices);

const isCompleteness = (value: unknown): boolean => {
    if (!isRow(value)) return false;
    if (value.status === "gaps_reported") {
        return isArrayOf(value.gaps, isCoverageGap) && typeof value.gapsTruncated === "boolean";
    }
    if (value.status === "no_gaps_reported") {
        return Array.isArray(value.gaps) && value.gaps.length === 0 && value.gapsTruncated === false;
    }
    return value.status === null && value.gaps === null && value.gapsTruncated === null;
};

const isFreshness = (value: unknown): boolean =>
    isRow(value) && isNullableNumber(value.lastReceivedAt) && isNullableNumber(value.lastIndexedAt);

const isClientLoss = (value: unknown): boolean =>
    isRow(value) && isNullableNumber(value.overflow) && isNullableNumber(value.expired) && isNullableNumber(value.dropped);

const isSummary = (value: unknown): value is FrontendErrorsSummaryWire =>
    isRow(value)
    && isSourceState(value)
    && isNumber(value.asOf)
    && isNumber(value.from)
    && isNumber(value.to)
    && isNullableNumber(value.total)
    && (value.byCategory === null || isDistribution(value.byCategory))
    && isNullableNumber(value.affectedDevicesApprox)
    && isAbsentOr(value.distinctFingerprints, isNullableNumber)
    && (value.freshness === null || isFreshness(value.freshness))
    && (value.clientLoss === null || isClientLoss(value.clientLoss))
    && isNullableNumber(value.consequencesSuppressed)
    && value.clientLossIsEstimate === true
    && isCompleteness(value.completeness);

const isSeriesCategoryCount = (value: unknown): value is { category: string; count: number | null } =>
    isRow(value) && typeof value.category === "string" && isNullableNumber(value.count);

const isSeriesBucket = (value: unknown): value is FrontendErrorsSeriesBucketWire =>
    isRow(value)
    && isNumber(value.start)
    && isNumber(value.end)
    && typeof value.madridLabel === "string"
    && isNullableNumber(value.count)
    && isAbsentOr(value.byCategory, (candidate) => candidate === null || isArrayOf(candidate, isSeriesCategoryCount))
    && isAbsentOr(value.devices, isNullableNumber);

const isSeries = (value: unknown): value is FrontendErrorsSeriesWire =>
    isRow(value)
    && isSourceState(value)
    && isNumber(value.asOf)
    && isNumber(value.from)
    && isNumber(value.to)
    && isNumber(value.bucketMs)
    && (value.buckets === null || isArrayOf(value.buckets, isSeriesBucket));

const isString = (value: unknown): value is string => typeof value === "string";

const isGroup = (value: unknown): value is FrontendErrorGroupWire =>
    isRow(value)
    && isNullableString(value.fingerprint)
    && isNullableString(value.category)
    && isNullableString(value.errorCode)
    && isNumber(value.count)
    && isNullableNumber(value.affectedDevicesApprox)
    && isNullableNumber(value.firstSeenAt)
    && isNullableNumber(value.lastSeenAt)
    && isAbsentOr(value.errorClass, isNullableString)
    && isAbsentOr(value.route, isNullableString)
    && isAbsentOr(value.httpStatus, isNullableNumber)
    && isAbsentOr(value.releases, (candidate) => isArrayOf(candidate, isString))
    && isAbsentOr(value.sampleMessage, isNullableString)
    && isAbsentOr(value.messageTemplate, isNullableString);

const isGroups = (value: unknown): value is FrontendErrorsGroupsWire =>
    isRow(value)
    && isSourceState(value)
    && isNumber(value.asOf)
    && isNullableNumber(value.total)
    && (value.groups === null || isArrayOf(value.groups, isGroup));

const isFrame = (value: unknown): value is FrontendErrorFrame =>
    isRow(value)
    && isNullableString(value.functionName)
    && isNullableString(value.asset)
    && isNullableNumber(value.line)
    && isNullableNumber(value.column);

const isEvent = (value: unknown): value is FrontendErrorEventWire =>
    isRow(value)
    && typeof value.receiptId === "string"
    && isNumber(value.effectiveOccurredAt)
    && isNullableNumber(value.occurredAt)
    && isNullableNumber(value.receivedAt)
    && isNullableString(value.category)
    && isNullableString(value.kind)
    && isNullableString(value.errorCode)
    && isNullableString(value.errorClass)
    && isNullableString(value.fingerprint)
    && isNullableString(value.route)
    && isNullableString(value.release)
    && isNullableString(value.platform)
    && isNullableString(value.source)
    && isNullableString(value.trust)
    && isAbsentOr(value.message, isNullableString)
    && isAbsentOr(value.httpStatus, isNullableNumber)
    && isAbsentOr(value.deviceId, isNullableString)
    && isAbsentOr(value.frames, (candidate) => candidate === null || isArrayOf(candidate, isFrame));

const isEvents = (value: unknown): value is FrontendErrorsEventsWire =>
    isRow(value)
    && isSourceState(value)
    && isNumber(value.asOf)
    && isNullableString(value.nextCursor)
    && (value.events === null || isArrayOf(value.events, isEvent));

const orNull = <T,>(value: T | undefined): T | null => (value === undefined ? null : value);

// The guards let the U1 additions be absent; these fill them with null ("not known"), never with 0 or [].
const toSummary = (value: FrontendErrorsSummaryWire): FrontendErrorsSummary => ({
    ...value,
    distinctFingerprints: orNull(value.distinctFingerprints),
});

const toSeries = (value: FrontendErrorsSeriesWire): FrontendErrorsSeries => ({
    ...value,
    buckets: value.buckets === null
        ? null
        : value.buckets.map((bucket) => ({ ...bucket, byCategory: orNull(bucket.byCategory), devices: orNull(bucket.devices) })),
});

const toGroups = (value: FrontendErrorsGroupsWire): FrontendErrorsGroups => ({
    ...value,
    groups: value.groups === null
        ? null
        : value.groups.map((group) => ({
            ...group,
            errorClass: orNull(group.errorClass),
            route: orNull(group.route),
            httpStatus: orNull(group.httpStatus),
            releases: orNull(group.releases),
            sampleMessage: orNull(group.sampleMessage),
            messageTemplate: orNull(group.messageTemplate),
        })),
});

const toEvents = (value: FrontendErrorsEventsWire): FrontendErrorsEvents => ({
    ...value,
    events: value.events === null
        ? null
        : value.events.map((event) => ({
            ...event,
            message: orNull(event.message),
            httpStatus: orNull(event.httpStatus),
            deviceId: orNull(event.deviceId),
            frames: orNull(event.frames),
        })),
});

export const frontendErrorsContract = {
    isSummary,
    isSeries,
    isGroups,
    isEvents,
    toSummary,
    toSeries,
    toGroups,
    toEvents,
};
